import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
  FaFileSignature, FaStar, FaRegClock, FaCheckCircle,
  FaExclamationTriangle, FaStopwatch, FaHourglassHalf, FaCalendarAlt,
  FaChevronLeft, FaChevronRight, FaLock, FaEye, FaEyeSlash, FaFilePdf,
  FaUserSlash, FaTimes,
} from 'react-icons/fa';
import EmployeeAvatar from '../../../components/EmployeeAvatar';
import { colors } from '../../employees/styles/personalakteStyles';
import styles from './WeeklyShifts.module.css';
import AbsenceCalendar from './AbsenceCalendar';
import ShiftAnalyticsService, {
  EmployeeAnalytics,
  AbsenceCalendarDay,
  buildMonthOptions,
} from '../services/ShiftAnalyticsService';
import { exportDashboardPdf, ExportBoard, ExportStanding } from '../services/dashboardPdfExport';

interface EmployeeLite {
  id: number;
  first_name: string;
  last_name: string;
  photo_url?: string;
}

interface ShiftDashboardProps {
  allEmployees?: EmployeeLite[];
}

interface LeaderboardConfig {
  key: string;
  title: string;
  subtitle: string;
  icon: React.ReactElement;
  accent: string;
  getValue: (emp: EmployeeAnalytics) => number;
  format: (emp: EmployeeAnalytics) => string;
  getTotal: (all: EmployeeAnalytics[]) => string;
  negative?: boolean;
}

const sumBy = (all: EmployeeAnalytics[], pick: (e: EmployeeAnalytics) => number): number =>
  all.reduce((acc, e) => acc + pick(e), 0);

const RANK_COLORS = ['#f59e0b', '#94a3b8', '#b45309'];
const MEDALS = ['🥇', '🥈', '🥉'];

function formatMinutes(totalMinutes: number): string {
  if (totalMinutes < 60) return `${totalMinutes} min`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
}

// Shown at the top under "Top performers" — safe to share with the team.
const POSITIVE_LEADERBOARDS: LeaderboardConfig[] = [
  {
    key: 'applicants',
    title: 'Top Applicants',
    subtitle: 'Most applications',
    icon: <FaFileSignature />,
    accent: '#5271A5',
    getValue: (e) => e.applications_total,
    format: (e) => `${e.applications_total} apps`,
    getTotal: (all) => `${sumBy(all, (e) => e.applications_total)} total`,
  },
  {
    key: 'weekendApps',
    title: 'Weekend Apps',
    subtitle: 'Applications for weekends',
    icon: <FaStar />,
    accent: '#E7007D',
    getValue: (e) => e.applications_weekend,
    format: (e) => `${e.applications_weekend} apps`,
    getTotal: (all) => `${sumBy(all, (e) => e.applications_weekend)} apps total`,
  },
  {
    key: 'weekend',
    title: 'Weekend Shifts',
    subtitle: 'Shifts worked on weekends',
    icon: <FaCheckCircle />,
    accent: '#db2777',
    getValue: (e) => e.weekend_shifts_worked,
    format: (e) => `${e.weekend_shifts_worked} shifts`,
    getTotal: (all) => `${sumBy(all, (e) => e.weekend_shifts_worked)} shifts total`,
  },
  {
    key: 'hours',
    title: 'Most Hours Worked',
    subtitle: 'Net worked hours',
    icon: <FaRegClock />,
    accent: '#0e7490',
    getValue: (e) => e.hours_worked,
    format: (e) => `${e.hours_worked.toFixed(1)} h`,
    getTotal: (all) => `${sumBy(all, (e) => e.hours_worked).toFixed(0)} h total`,
  },
  {
    key: 'weekendHours',
    title: 'Weekend Hours',
    subtitle: 'Hours worked on weekends',
    icon: <FaRegClock />,
    accent: '#be185d',
    getValue: (e) => e.weekend_hours_worked,
    format: (e) => `${e.weekend_hours_worked.toFixed(1)} h`,
    getTotal: (all) => `${sumBy(all, (e) => e.weekend_hours_worked).toFixed(0)}h / ${sumBy(all, (e) => e.hours_worked).toFixed(0)}h`,
  },
  {
    key: 'punctual',
    title: 'Most Punctual',
    subtitle: 'On-time rate',
    icon: <FaCheckCircle />,
    accent: '#16a34a',
    // Bayesian-smoothed on-time rate: add 2 virtual on-time + 2 virtual late
    // shifts. A high rate ranks first (6/6 beats 7/8), but tiny samples can't
    // dominate (1/1 no longer beats 7/8); larger samples win at an equal rate.
    getValue: (e) => (e.shifts_matched > 0 && e.punctual_count > 0 ? (e.punctual_count + 2) / (e.shifts_matched + 4) : 0),
    format: (e) => `${e.punctual_count}/${e.shifts_matched} on time`,
    getTotal: (all) => `${sumBy(all, (e) => e.punctual_count)}/${sumBy(all, (e) => e.shifts_matched)} on time`,
  },
  {
    key: 'cleanEntries',
    title: 'Saubere Zeiterfassung',
    subtitle: 'Wenigste Korrekturen',
    icon: <FaCheckCircle />,
    accent: '#0d9488',
    // Bayesian-smoothed clean rate: clean shifts (no change request) over total,
    // so a spotless small record can't beat a big mostly-clean one at parity.
    getValue: (e) => {
      const total = e.time_trackings_count;
      const clean = Math.max(0, total - e.change_requests_count);
      return total > 0 ? (clean + 2) / (total + 4) : 0;
    },
    format: (e) => `${Math.max(0, e.time_trackings_count - e.change_requests_count)}/${e.time_trackings_count} sauber`,
    getTotal: (all) => `${sumBy(all, (e) => e.change_requests_count)} Korrekturen`,
  },
];

// Leaderboards included in the shareable PDF export (6 for a 2×3 page), in order.
const EXPORT_KEYS = ['applicants', 'weekendApps', 'weekend', 'hours', 'weekendHours', 'punctual'];

// Employee IDs excluded from the dashboard in every month. They disappear from
// every category, the totals and the overall points account.
const GLOBAL_EXCLUDED_EMPLOYEE_IDS: number[] = [150];

// Employee IDs excluded only in a specific month (key = YYYY-MM).
const PERIOD_EXCLUDED_EMPLOYEE_IDS: Record<string, number[]> = {};

// The "Most Hours Worked" board can be trimmed of full-time production staff and
// the owner via an on-screen control. The chosen IDs are stored per browser.
const HOURS_CATEGORY_KEY = 'hours';
const HOURS_EXCLUDE_STORAGE_KEY = 'sundae.dashboard.hoursExcludedIds';

function loadHoursExcludedIds(): number[] {
  try {
    const raw = localStorage.getItem(HOURS_EXCLUDE_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((n) => typeof n === 'number') : [];
  } catch {
    return [];
  }
}

function hexToRgb(hex: string): [number, number, number] {
  const m = hex.replace('#', '');
  return [
    parseInt(m.slice(0, 2), 16),
    parseInt(m.slice(2, 4), 16),
    parseInt(m.slice(4, 6), 16),
  ];
}

// Points earned per rank across every exported category (gold 3, silver 2,
// bronze 1). Whoever tops the sum may wish for an ice cream flavour.
const RANK_POINTS = [3, 2, 1];

// A standing carries the PDF fields plus identity data so the on-screen card
// can render an avatar. The extra fields are ignored by the PDF export.
interface Standing extends ExportStanding {
  employeeId: number;
  firstName: string;
  lastName: string;
}

/**
 * Turns the per-category rankings into an overall points standing. Each
 * category awards 3/2/1 to its top three; totals are summed per employee and
 * sorted by points, then by medal quality as the tie-breaker.
 */
function computeStandings(rankedByConfig: EmployeeAnalytics[][]): Standing[] {
  const byEmployee = new Map<number, Standing>();

  rankedByConfig.forEach((ranked) => {
    ranked.slice(0, RANK_POINTS.length).forEach((emp, rank) => {
      const existing = byEmployee.get(emp.employee_id) || {
        employeeId: emp.employee_id,
        firstName: emp.first_name,
        lastName: emp.last_name,
        name: `${emp.first_name} ${emp.last_name}`,
        points: 0,
        gold: 0,
        silver: 0,
        bronze: 0,
        place: 0,
      };
      existing.points += RANK_POINTS[rank];
      if (rank === 0) existing.gold += 1;
      else if (rank === 1) existing.silver += 1;
      else existing.bronze += 1;
      byEmployee.set(emp.employee_id, existing);
    });
  });

  const sorted = Array.from(byEmployee.values()).sort(
    (a, b) => b.points - a.points || b.gold - a.gold || b.silver - a.silver,
  );

  // Dense ranking: equal points share a place and the next distinct score gets
  // the next consecutive place — no gaps (1, 2, 2, 3 …). Medals only order tied
  // rows, they don't affect the place number.
  let place = 0;
  let prevPoints = Number.NaN;
  sorted.forEach((s) => {
    if (s.points !== prevPoints) { place += 1; prevPoints = s.points; }
    s.place = place;
  });

  return sorted;
}

// Confidential — shown at the very bottom, not meant to be shared with the team.
const INTERNAL_LEADERBOARDS: LeaderboardConfig[] = [
  {
    key: 'unpunctual',
    title: 'Most Unpunctual',
    subtitle: 'Late clock-ins',
    icon: <FaExclamationTriangle />,
    accent: '#dc2626',
    getValue: (e) => e.late_count,
    format: (e) => `${e.late_count} late`,
    getTotal: (all) => `${sumBy(all, (e) => e.late_count)}/${sumBy(all, (e) => e.shifts_matched)} late`,
    negative: true,
  },
  {
    key: 'deviation',
    title: 'Biggest Start Deviation',
    subtitle: 'Largest late clock-in',
    icon: <FaStopwatch />,
    accent: '#ea580c',
    getValue: (e) => e.max_start_deviation_min,
    format: (e) => `${e.max_start_deviation_min} min late`,
    getTotal: (all) => `${sumBy(all, (e) => e.late_count)} late clock-ins`,
    negative: true,
  },
  {
    key: 'overtime',
    title: 'Überstunden',
    subtitle: 'Worked beyond scheduled end',
    icon: <FaHourglassHalf />,
    accent: '#7c3aed',
    getValue: (e) => e.overtime_min,
    format: (e) => formatMinutes(e.overtime_min),
    getTotal: (all) => `${formatMinutes(sumBy(all, (e) => e.overtime_min))} total`,
    negative: true,
  },
  {
    key: 'absence',
    title: 'Abwesenheitsquote',
    subtitle: 'Fehltage im Zeitraum (alle Abwesenheiten)',
    icon: <FaCalendarAlt />,
    accent: '#64748b',
    getValue: (e) => e.absence_rate_pct,
    format: (e) => `${e.absence_days}d · ${e.absence_rate_pct}%`,
    getTotal: (all) => `${sumBy(all, (e) => e.absence_days)}d total`,
    negative: true,
  },
];

export default function ShiftDashboard({ allEmployees = [] }: ShiftDashboardProps): React.ReactElement {
  const monthOptions = useMemo(() => buildMonthOptions(12, new Date()), []);
  const [selectedKey, setSelectedKey] = useState<string>(monthOptions[0].key);
  const [employees, setEmployees] = useState<EmployeeAnalytics[]>([]);
  const [absenceDays, setAbsenceDays] = useState<AbsenceCalendarDay[]>([]);
  const [employeeCount, setEmployeeCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showInternal, setShowInternal] = useState<boolean>(true);
  const [hoursExcludedIds, setHoursExcludedIds] = useState<number[]>(loadHoursExcludedIds);
  const [showHoursExclude, setShowHoursExclude] = useState<boolean>(false);

  useEffect(() => {
    try { localStorage.setItem(HOURS_EXCLUDE_STORAGE_KEY, JSON.stringify(hoursExcludedIds)); } catch { /* ignore */ }
  }, [hoursExcludedIds]);

  const toggleHoursExcluded = (id: number) => {
    setHoursExcludedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const currentIndex = Math.max(0, monthOptions.findIndex((o) => o.key === selectedKey));
  const activePeriod = monthOptions[currentIndex] || monthOptions[0];
  const goOlder = () => { if (currentIndex < monthOptions.length - 1) setSelectedKey(monthOptions[currentIndex + 1].key); };
  const goNewer = () => { if (currentIndex > 0) setSelectedKey(monthOptions[currentIndex - 1].key); };

  const photoById = useMemo(() => {
    const map: Record<number, string | undefined> = {};
    allEmployees.forEach((e) => { map[e.id] = e.photo_url; });
    return map;
  }, [allEmployees]);

  const visibleEmployees = useMemo(() => {
    const excludedIds = new Set([
      ...GLOBAL_EXCLUDED_EMPLOYEE_IDS,
      ...(PERIOD_EXCLUDED_EMPLOYEE_IDS[activePeriod.key] || []),
    ]);
    if (excludedIds.size === 0) return employees;
    return employees.filter((e) => !excludedIds.has(e.employee_id));
  }, [employees, activePeriod.key]);

  const hoursExcludedSet = useMemo(() => new Set(hoursExcludedIds), [hoursExcludedIds]);

  // The employee pool a board ranks over. "Most Hours Worked" additionally drops
  // the manually excluded staff (full-time production / owner) so the board, the
  // points account and the PDF all stay fair.
  const poolFor = (config: LeaderboardConfig): EmployeeAnalytics[] =>
    config.key === HOURS_CATEGORY_KEY
      ? visibleEmployees.filter((e) => !hoursExcludedSet.has(e.employee_id))
      : visibleEmployees;

  // The exported categories and the overall points account, shared by both the
  // on-screen "Gesamtkonto" card and the PDF export so they never drift apart.
  const { exportBoards, standings } = useMemo(() => {
    const byKey = new Map(POSITIVE_LEADERBOARDS.map((c) => [c.key, c]));
    const configs = EXPORT_KEYS
      .map((key) => byKey.get(key))
      .filter((cfg): cfg is LeaderboardConfig => Boolean(cfg));

    const rankedByConfig = configs.map((cfg) =>
      poolFor(cfg)
        .filter((e) => cfg.getValue(e) > 0)
        .sort((a, b) => cfg.getValue(b) - cfg.getValue(a)),
    );

    const boards: ExportBoard[] = configs.map((cfg, i) => ({
      title: cfg.title,
      subtitle: cfg.subtitle,
      accent: hexToRgb(cfg.accent),
      rows: rankedByConfig[i].slice(0, 5).map((e) => ({
        name: `${e.first_name} ${e.last_name}`,
        value: cfg.getValue(e),
        label: cfg.format(e),
      })),
    }));

    return { exportBoards: boards, standings: computeStandings(rankedByConfig) };
  }, [visibleEmployees, hoursExcludedSet]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    ShiftAnalyticsService.fetchAnalytics(activePeriod.start, activePeriod.end)
      .then((res) => {
        if (!cancelled) setEmployees(res.employees || []);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load analytics.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    ShiftAnalyticsService.fetchAbsenceCalendar(activePeriod.start, activePeriod.end)
      .then((res) => {
        if (cancelled) return;
        setAbsenceDays(res.days || []);
        setEmployeeCount(res.employee_count || 0);
      })
      .catch(() => {
        if (!cancelled) { setAbsenceDays([]); setEmployeeCount(0); }
      });
    return () => { cancelled = true; };
  }, [activePeriod.start, activePeriod.end]);

  const handleExportPdf = () => {
    exportDashboardPdf(exportBoards, activePeriod.label, standings);
  };

  const renderMonthSelect = () => {
    const olderDisabled = currentIndex >= monthOptions.length - 1;
    const newerDisabled = currentIndex <= 0;
    return (
      <div className={styles.weekNav}>
        <button
          className={styles.weekNavBtn}
          onClick={goOlder}
          disabled={olderDisabled}
          aria-label="Previous month"
        >
          <FaChevronLeft className={styles.iconSmall} />
        </button>
        <div className={styles.cwButton} style={{ position: 'relative' }}>
          <FaCalendarAlt className={styles.calendarIconSmall} />
          <select
            value={selectedKey}
            onChange={(e) => setSelectedKey(e.target.value)}
            style={{
              appearance: 'none',
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              border: 'none',
              background: 'transparent',
              font: 'inherit',
              fontWeight: 600,
              color: 'inherit',
              cursor: 'pointer',
              outline: 'none',
              paddingRight: '14px',
            }}
          >
            {monthOptions.map((opt) => (
              <option key={opt.key} value={opt.key}>{opt.label}</option>
            ))}
          </select>
          <span style={{ position: 'absolute', right: '12px', fontSize: '9px', pointerEvents: 'none' }}>▼</span>
        </div>
        <button
          className={styles.weekNavBtn}
          onClick={goNewer}
          disabled={newerDisabled}
          aria-label="Next month"
        >
          <FaChevronRight className={styles.iconSmall} />
        </button>
      </div>
    );
  };

  const renderHoursExcludePanel = (accent: string) => {
    const candidates = visibleEmployees
      .filter((e) => e.hours_worked > 0)
      .sort((a, b) => b.hours_worked - a.hours_worked);
    return (
      <div style={{
        marginBottom: '14px', border: `1px solid ${accent}33`,
        background: `${accent}0a`, borderRadius: '10px', padding: '10px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <span style={{ fontSize: '11px', fontWeight: 700, color: colors.navy }}>Aus Wertung ausschließen</span>
          {hoursExcludedIds.length > 0 && (
            <button
              onClick={() => setHoursExcludedIds([])}
              style={{
                fontSize: '10px', fontWeight: 600, color: '#64748b',
                background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
              }}
            >
              Zurücksetzen
            </button>
          )}
        </div>
        <div style={{ maxHeight: '180px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '3px' }}>
          {candidates.length === 0 ? (
            <div style={{ fontSize: '11px', color: '#94a3b8', fontStyle: 'italic' }}>Keine Stunden im Zeitraum</div>
          ) : candidates.map((e) => {
            const excluded = hoursExcludedSet.has(e.employee_id);
            return (
              <button
                key={e.employee_id}
                onClick={() => toggleHoursExcluded(e.employee_id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px', textAlign: 'left', width: '100%',
                  background: excluded ? `${accent}1f` : 'transparent', border: 'none',
                  borderRadius: '7px', padding: '5px 7px', cursor: 'pointer',
                }}
              >
                <span style={{
                  width: '16px', height: '16px', borderRadius: '4px', flexShrink: 0,
                  border: `1.5px solid ${excluded ? accent : '#cbd5e1'}`,
                  background: excluded ? accent : '#fff', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px',
                }}>
                  {excluded ? <FaTimes /> : null}
                </span>
                <span style={{
                  flex: 1, minWidth: 0, fontSize: '12px', fontWeight: 600,
                  color: excluded ? '#94a3b8' : '#1e293b',
                  textDecoration: excluded ? 'line-through' : 'none',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {e.first_name} {e.last_name}
                </span>
                <span style={{ fontSize: '11px', color: '#94a3b8', flexShrink: 0 }}>{e.hours_worked.toFixed(1)} h</span>
              </button>
            );
          })}
        </div>
        <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '8px', lineHeight: 1.4 }}>
          Ausgeschlossene zählen nicht für „Most Hours Worked" — inkl. Punkte &amp; PDF. Lokal gespeichert.
        </div>
      </div>
    );
  };

  const renderCard = (config: LeaderboardConfig) => {
    const pool = poolFor(config);
    const ranked = pool
      .filter((e) => config.getValue(e) > 0)
      .sort((a, b) => config.getValue(b) - config.getValue(a));
    const maxValue = ranked.length > 0 ? config.getValue(ranked[0]) : 0;
    const isHours = config.key === HOURS_CATEGORY_KEY;

    return (
      <div
        key={config.key}
        style={{
          background: '#fff',
          border: '1px solid rgba(226, 232, 240, 0.9)',
          borderRadius: '14px',
          padding: '16px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
          <div style={{
            width: '34px', height: '34px', borderRadius: '9px',
            background: `${config.accent}1a`, color: config.accent,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px',
            flexShrink: 0,
          }}>
            {config.icon}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: colors.navy }}>{config.title}</div>
            <div style={{ fontSize: '11px', color: '#94a3b8' }}>{config.subtitle}</div>
          </div>
          {isHours && (
            <button
              onClick={() => setShowHoursExclude((v) => !v)}
              title="Mitarbeiter aus dieser Wertung ausschließen"
              style={{
                display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0,
                fontSize: '11px', fontWeight: 600,
                color: showHoursExclude ? '#fff' : config.accent,
                background: showHoursExclude ? config.accent : `${config.accent}14`,
                border: 'none', borderRadius: '8px', padding: '4px 9px', cursor: 'pointer',
              }}
            >
              <FaUserSlash />
              {hoursExcludedIds.length > 0 ? hoursExcludedIds.length : ''}
            </button>
          )}
          <div style={{
            fontSize: '11px', fontWeight: 700, color: config.accent,
            background: `${config.accent}14`, padding: '4px 9px', borderRadius: '8px',
            whiteSpace: 'nowrap', flexShrink: 0,
          }}>
            {config.getTotal(pool)}
          </div>
        </div>

        {isHours && showHoursExclude && renderHoursExcludePanel(config.accent)}

        {ranked.length === 0 ? (
          <div style={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic', padding: '12px 0' }}>
            No data for this period
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '264px', overflowY: 'auto', paddingRight: '4px' }}>
            {ranked.map((emp, index) => {
              const value = config.getValue(emp);
              const barPercent = maxValue > 0 ? Math.max(5, (value / maxValue) * 100) : 0;
              const medal = !config.negative && index < 3 ? MEDALS[index] : null;
              return (
                <div key={emp.employee_id} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{
                    width: '20px', textAlign: 'center', flexShrink: 0,
                    fontSize: medal ? '15px' : '12px', fontWeight: 800,
                    color: medal ? RANK_COLORS[index] : '#cbd5e1',
                  }}>
                    {medal || index + 1}
                  </div>
                  <EmployeeAvatar
                    firstName={emp.first_name}
                    lastName={emp.last_name}
                    photoUrl={photoById[emp.employee_id]}
                    size={28}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {emp.first_name} {emp.last_name}
                      </span>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: config.accent, whiteSpace: 'nowrap', flexShrink: 0 }}>
                        {config.format(emp)}
                      </span>
                    </div>
                    <div style={{ height: '6px', background: 'rgba(0,0,0,0.05)', borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{
                        width: `${barPercent}%`, height: '100%',
                        background: index === 0 ? config.accent : `${config.accent}b3`,
                        borderRadius: '3px', transition: 'width 0.3s ease',
                      }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderStandings = () => {
    // Show the top five places; equal points share a place, so a tie can list
    // more than five people (all sharing that place still appear).
    const top = standings.filter((s) => s.place <= 5);
    const maxPoints = top.length > 0 ? top[0].points : 0;
    const medalChips = (s: Standing) => (
      <div style={{ display: 'flex', gap: '5px', alignItems: 'center', flexShrink: 0 }}>
        {([['gold', s.gold], ['silver', s.silver], ['bronze', s.bronze]] as const)
          .filter(([, count]) => count > 0)
          .map(([tone, count], i) => (
            <span key={i} style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: '18px', height: '18px', borderRadius: '50%',
              background: { gold: '#f59e0b', silver: '#94a3b8', bronze: '#b45309' }[tone],
              color: '#fff', fontSize: '10px', fontWeight: 800,
            }}>
              {count}
            </span>
          ))}
      </div>
    );

    return (
      <div style={{
        marginTop: '24px',
        background: '#fff',
        border: '1px solid rgba(231, 0, 125, 0.35)',
        borderRadius: '14px',
        padding: '18px',
        boxShadow: '0 2px 10px rgba(231, 0, 125, 0.06)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
          <div style={{
            width: '34px', height: '34px', borderRadius: '9px',
            background: 'rgba(231, 0, 125, 0.1)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: '17px', flexShrink: 0,
          }}>
            🍦
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: '15px', fontWeight: 700, color: colors.navy }}>Gesamtkonto</div>
            <div style={{ fontSize: '11px', color: '#94a3b8' }}>
              Punkte gesamt (Gold 3 · Silber 2 · Bronze 1) — Team Member mit meisten Punkten darf sich eine Eissorte wünschen
            </div>
          </div>
        </div>

        {top.length === 0 ? (
          <div style={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic', padding: '12px 0' }}>
            No data for this period
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '12px' }}>
            {top.map((s) => {
              const winner = s.place === 1;
              const barPercent = maxPoints > 0 ? Math.max(5, (s.points / maxPoints) * 100) : 0;
              const medal = s.place <= 3 ? MEDALS[s.place - 1] : null;
              return (
                <div key={s.employeeId} style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  background: winner ? 'rgba(231, 0, 125, 0.06)' : 'transparent',
                  border: winner ? '1px solid rgba(231, 0, 125, 0.25)' : '1px solid transparent',
                  borderRadius: '10px', padding: '8px 10px',
                }}>
                  <div style={{
                    width: '22px', textAlign: 'center', flexShrink: 0,
                    fontSize: medal ? '16px' : '13px', fontWeight: 800,
                    color: medal ? RANK_COLORS[s.place - 1] : '#cbd5e1',
                  }}>
                    {medal || s.place}
                  </div>
                  <EmployeeAvatar
                    firstName={s.firstName}
                    lastName={s.lastName}
                    photoUrl={photoById[s.employeeId]}
                    size={30}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
                      <span style={{ fontSize: '13.5px', fontWeight: 700, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {s.name}
                      </span>
                      {winner && (
                        <span style={{
                          fontSize: '10px', fontWeight: 800, color: '#fff', letterSpacing: '0.4px',
                          background: '#E7007D', padding: '2px 8px', borderRadius: '10px',
                          whiteSpace: 'nowrap', flexShrink: 0,
                        }}>
                          🍦 EISSORTE
                        </span>
                      )}
                    </div>
                    <div style={{ height: '6px', background: 'rgba(0,0,0,0.05)', borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{
                        width: `${barPercent}%`, height: '100%',
                        background: winner ? '#E7007D' : 'rgba(82, 113, 165, 0.7)',
                        borderRadius: '3px', transition: 'width 0.3s ease',
                      }} />
                    </div>
                  </div>
                  {medalChips(s)}
                  <div style={{
                    fontSize: '15px', fontWeight: 800, color: colors.navy,
                    minWidth: '52px', textAlign: 'right', flexShrink: 0,
                  }}>
                    {s.points} <span style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8' }}>Pkt</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: '16px', boxSizing: 'border-box', width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: colors.navy }}>Dashboard</h2>
          <div style={{ fontSize: '12px', color: '#94a3b8' }}>Top performers · {activePeriod.label}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <button
            onClick={handleExportPdf}
            disabled={loading || visibleEmployees.length === 0}
            title="Team-Export als PDF (Top 5)"
            style={{
              display: 'flex', alignItems: 'center', gap: '7px',
              fontSize: '13px', fontWeight: 600, color: '#fff',
              background: colors.navy, border: 'none', borderRadius: '10px',
              padding: '9px 14px', cursor: (loading || visibleEmployees.length === 0) ? 'default' : 'pointer',
              opacity: (loading || visibleEmployees.length === 0) ? 0.5 : 1,
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            }}
          >
            <FaFilePdf /> Export PDF
          </button>
          {renderMonthSelect()}
        </div>
      </div>

      {error && (
        <div style={{ fontSize: '13px', color: '#dc2626', padding: '10px 0' }}>{error}</div>
      )}
      {loading && (
        <div style={{ fontSize: '13px', color: '#64748b', padding: '10px 0' }}>Loading…</div>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        gap: '16px',
        opacity: loading ? 0.5 : 1,
        transition: 'opacity 0.15s ease',
      }}>
        {POSITIVE_LEADERBOARDS.map(renderCard)}
      </div>

      <div style={{ opacity: loading ? 0.5 : 1, transition: 'opacity 0.15s ease' }}>
        {renderStandings()}
      </div>

      <div style={{ marginTop: '16px', opacity: loading ? 0.5 : 1, transition: 'opacity 0.15s ease' }}>
        <AbsenceCalendar
          days={absenceDays}
          employeeCount={employeeCount}
          label={activePeriod.label}
        />
      </div>

      <div style={{
        marginTop: '32px',
        paddingTop: '20px',
        borderTop: '1px dashed rgba(148, 163, 184, 0.5)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px', flexWrap: 'wrap' }}>
          <FaLock style={{ color: '#94a3b8', fontSize: '13px' }} />
          <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: colors.navy }}>Intern</h3>
          <span style={{
            fontSize: '11px', fontWeight: 600, color: '#b45309',
            background: 'rgba(180, 83, 9, 0.1)', padding: '3px 8px', borderRadius: '6px',
          }}>
            Nicht mit dem Team teilen
          </span>
          <button
            onClick={() => setShowInternal((v) => !v)}
            style={{
              marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px',
              fontSize: '12px', fontWeight: 600, color: '#64748b',
              background: 'transparent', border: '1px solid rgba(148, 163, 184, 0.5)',
              borderRadius: '8px', padding: '5px 10px', cursor: 'pointer',
            }}
          >
            {showInternal ? <FaEyeSlash /> : <FaEye />}
            {showInternal ? 'Ausblenden' : 'Einblenden'}
          </button>
        </div>
        <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '16px' }}>
          Pünktlichkeit, Abwesenheit &amp; Überstunden — nur für die interne Auswertung.
        </div>

        {showInternal && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: '16px',
            opacity: loading ? 0.5 : 1,
            transition: 'opacity 0.15s ease',
          }}>
            {INTERNAL_LEADERBOARDS.map(renderCard)}
          </div>
        )}
      </div>
    </div>
  );
}
